
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Trash2, Bell, Trophy, BookOpen, Calendar } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface NotificationCardProps {
  notification: {
    id: string;
    title: string;
    description: string;
    created_at: string;
    read: boolean;
    type: 'progress_update' | 'reminder' | 'suggestion' | 'achievement';
  };
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

export const NotificationCard = ({ notification, onMarkAsRead, onDelete }: NotificationCardProps) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'progress_update':
        return <BookOpen className="h-5 w-5 text-blue-500" />;
      case 'reminder':
        return <Calendar className="h-5 w-5 text-orange-500" />;
      case 'suggestion':
        return <Bell className="h-5 w-5 text-purple-500" />;
      case 'achievement':
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const getBackgroundColor = () => {
    if (notification.read) return "bg-gray-50";
    
    switch (notification.type) {
      case 'progress_update':
        return "bg-blue-50";
      case 'reminder':
        return "bg-orange-50";
      case 'suggestion':
        return "bg-purple-50";
      case 'achievement':
        return "bg-yellow-50";
      default:
        return "bg-white";
    }
  };

  return (
    <Card className={`p-4 ${getBackgroundColor()} transition-colors duration-200`}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 mt-1">
            {getIcon()}
          </div>
          <div className="space-y-2 min-w-0">
            <h3 className="font-semibold break-words">{notification.title}</h3>
            <p className="text-gray-600 break-words">{notification.description}</p>
            <p className="text-sm text-gray-400">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-2 sm:mt-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onMarkAsRead(notification.id)}
            disabled={notification.read}
            className="flex items-center gap-2 flex-1 sm:flex-none justify-center"
          >
            <Check className="h-4 w-4" />
            <span className="sm:hidden">
              {notification.read ? "Read" : "Mark as Read"}
            </span>
            <span className="hidden sm:inline">
              {notification.read ? "Read" : "Mark as Read"}
            </span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(notification.id)}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
          >
            <Trash2 className="h-4 w-4" />
            <span className="ml-2 sm:hidden">Delete</span>
          </Button>
        </div>
      </div>
    </Card>
  );
};
